/**
 * Game player — the streaming experience for a single game.
 *
 * Four phases: "intro" → "mic-setup" → "connecting" → "streaming"
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router";
import { Turnstile } from "@marsidev/react-turnstile";
import { MicSetup } from "./MicSetup";
import { StreamView } from "./StreamView";
import { ControlsDiagram } from "./ControlsDiagram";
import { useStreamSession } from "../hooks/useStreamSession";
import { useStreamStats } from "../hooks/useStreamStats";
import { measureLatencies, type LatencyResult } from "../lib/latency";
import { generatePrivacyCode } from "../lib/privacyCode";
import { isMobile } from "../lib/isMobile";
import labsLogo from "../assets/labs-logo.svg";
import type { GameConfig } from "@shared/games";

type Phase = "intro" | "age-verification" | "blocked" | "consent" | "mic-setup" | "connecting" | "streaming";

interface GamePlayerProps {
  slug: string;
  game: GameConfig;
}

export function GamePlayer({ slug, game }: GamePlayerProps) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("intro");
  const [latencyResults, setLatencyResults] = useState<LatencyResult[]>([]);
  const [privacyCode, setPrivacyCode] = useState("");
  const [userId, setUserId] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useEffect(() => {
    measureLatencies(game.streamLocations).then(setLatencyResults);
    fetch(`/api/session/warmup/${slug}`, { method: "POST" }).catch(() => {});
  }, [game.streamLocations, slug]);

  const { status, statusReason, capacityStatus, sessionLocation, glsInstance, perfStats, startSession, endSession } =
    useStreamSession(slug);

  const streamStats = useStreamStats(phase === "streaming" ? glsInstance : null);

  const handleMicReady = useCallback(
    async (deviceId: string, cleanupMic: () => void) => {
      if (!turnstileToken) return;
      cleanupMic();
      setPhase("connecting");
      await new Promise((r) => setTimeout(r, 50));
      // Tokens are single-use; clear it so a retry requires a fresh challenge.
      setTurnstileToken(null);
      await startSession(deviceId, latencyResults, userId, turnstileToken);
    },
    [startSession, latencyResults, userId, turnstileToken]
  );

  // Age is never stored or sent anywhere — the answer only picks the next phase.
  const handleAgeChoice = useCallback((isAdult: boolean) => {
    if (isAdult) {
      setPrivacyCode(generatePrivacyCode());
      setPhase("consent");
    } else {
      setPhase("blocked");
    }
  }, []);

  const handleDownloadCode = useCallback(() => {
    const blob = new Blob(
      [`Your Pressure Point privacy reference code: ${privacyCode}\n`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Your Pressure Point Privacy Code.txt";
    a.click();
    URL.revokeObjectURL(url);
  }, [privacyCode]);

  const handleConsentChoice = useCallback(
    (contribute: boolean) => {
      setUserId(contribute ? `${privacyCode}-consent` : privacyCode);
      setPhase("mic-setup");
    },
    [privacyCode]
  );

  useEffect(() => {
    if (status === "ACTIVE") {
      setPhase("streaming");
      if (isMobile()) {
        // Nudge scroll to trigger Safari toolbar collapse
        setTimeout(() => window.scrollTo(0, 1), 100);
      }
    }
  }, [status]);

  const handleEndStream = useCallback(() => {
    endSession();
    setPhase("intro");
  }, [endSession]);

  const handleBack = useCallback(() => {
    endSession();
    navigate("/");
  }, [endSession, navigate]);

  const showStreamElements = phase === "connecting" || phase === "streaming";
  const gameLoaded = status === "ACTIVE";

  const cpuPercent = perfStats
    ? (perfStats.application.cpuUtilizationNormalized ?? 0) * 100
    : 0;
  const gpuPercent = perfStats?.system.gpuUtilization ?? 0;

  const isError = status === "ERROR" || status === "FULL";

  return (
    <div className={`pp-shell ${isMobile() && phase === "streaming" ? "app-mobile-fullscreen" : ""}`}>
      <div className="pp-nav">
        <button className="pp-back" onClick={handleBack} aria-label="Back">
          <svg className="pp-back-icon" viewBox="0 0 8 9">
            <path d="M7.13761 0V1.38787L1.68923 4.29049L1.07064 4.44911L1.6813 4.60772L7.13761 7.49449V8.88236L0 5.25804V3.60053L7.13761 0Z" fill="white"/>
          </svg>
        </button>
        <img src={labsLogo} alt="Iconic Labs" className="pp-nav-logo" />
      </div>

      {phase === "streaming" && streamStats && (
        <div className="pp-stats">
          <span className={streamStats.pingMs > 50 ? "stat-warn" : ""}>
            {streamStats.pingMs.toFixed(0)}ms
          </span>
          <span>{streamStats.fps.toFixed(0)}fps</span>
          <span>{streamStats.bitrateMbps.toFixed(1)}mbps</span>
          {perfStats && (
            <>
              <span className={cpuPercent > 50 ? "stat-warn" : ""}>
                cpu {cpuPercent.toFixed(0)}%
              </span>
              <span>gpu {gpuPercent.toFixed(0)}%</span>
            </>
          )}
        </div>
      )}

      <div className="pp-stage">
        <div className={`pp-content pp-content--wide`}>
          {phase === "intro" && (
            <>
              <h1 className="pp-headline">
                Pressure Point is an experimental experience.
              </h1>
              James is an AI character and his responses are generated in real time. While we've tested the experience extensively, we can't guarantee every response will be appropriate. You can end the session at any time, and you can report a concerning response at feedback@iconicgames.io.
              <a className="pp-read-more" href="https://iconicgames.io/blog/pressure-point" target="_blank" rel="noopener noreferrer">Read about Pressure Point on our blog.</a>
              <div className="pp-mono-lines">
                {isMobile()
                  ? 
                    <>
                      <p className="pp-mobile-warning">
                        Important: This experience is best suited for desktop devices, not mobile. Switch for a better experience.
                      </p> 
                      <p>
                        Requires microphone access.<br />Recommended: &gt;15 Mbps connection.
                      </p>
                    </>
                  : <>Best enjoyed in a quiet room.<br />Requires mouse/keyboard and microphone.<br />Recommended: &gt;15 Mbps connection.</>
                }
              </div>
              <button
                className="pp-btn"
                onClick={() => setPhase("age-verification")}
              >
                Start
              </button>
            </>
          )}

          {phase === "age-verification" && (
            <>
              <h1 className="pp-headline">
                Confirm Your Age
              </h1>
              <div className="pp-mono-lines">
                We ask your age to make sure the game and any data settings are appropriate for you.
              </div>
              <div className="pp-age-row">
                <button className="pp-btn" onClick={() => handleAgeChoice(false)}>
                  I'm under 18
                </button>
                <button className="pp-btn" onClick={() => handleAgeChoice(true)}>
                  I'm 18 or over
                </button>
              </div>
              <div className="pp-privacy-note">
                Your answer is used only on this device and is never stored or sent anywhere.<br></br>By continuing, you confirm your answer is accurate.
              </div>
            </>
          )}

          {phase === "blocked" && (
            <>
              <h1 className="pp-headline">
                Sorry, Pressure Point is not yet<br />available for your age group.
              </h1>
              <div className="pp-mono-lines">
                We appreciate your interest on Pressure Point. You can can still learn more by <a className="pp-read-more" href="https://iconicgames.io/blog/pressure-point" target="_blank" rel="noopener noreferrer">reading our blog</a> or explore other experiences here:
              </div>
              <button
                className="pp-btn"
                onClick={() => navigate("/")}
              >
                Explore Experiences
              </button>
            </>
          )}

          {phase === "consent" && (
            <>
              <h1 className="pp-headline">Your voice and your data
              </h1>
              <div className="pp-consent-text">
                <p>
                Pressure Point is an AI-enabled demo where you speak to an AI character using your microphone. We process your voice input while you push-to-talk so the character can respond and the demo can work.
                </p>
                <p>
                  <b>Help build Iconic's AI characters</b>
                </p>
                <p>
                You can optionally choose to contribute your play session to help us create better characters and games. If you agree, we will keep your push-to-talk audio recordings and transcripts and use them in our commercial model development of systems for interactive characters. Since you can say anything while playing, this may include sensitive details you choose to share (e.g. your background or beliefs) - so we need your explicit consent. Your data is stored securely. Full details in our <a className="pp-read-more" href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.</p>
                <p>
                If you decline, you can still play the full demo, and we won't retain any of your data.</p>
                <p>
We don't collect your name or contact details. To withdraw consent or request deletion later, you'll need this privacy reference code - click on the code to download it: <span
                  className="pp-privacy-code"
                  role="button"
                  tabIndex={0}
                  onClick={handleDownloadCode}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDownloadCode();
                    }
                  }}
                  title="Click to download your code"
                >{privacyCode}</span>.

                </p>
              </div>
              <div className="pp-consent-actions">
                <button className="pp-btn dim " onClick={() => handleConsentChoice(false)}>
                  Play without contributing
                </button>
                <button className="pp-btn" onClick={() => handleConsentChoice(true)}>
                  Agree and contribute
                </button>
              </div>
            </>
          )}

          {phase === "mic-setup" && (
            <>
              <MicSetup
                onReady={handleMicReady}
                locationsReady={latencyResults.length > 0}
                bestRegion={latencyResults[0] ?? null}
                humanVerified={turnstileToken !== null}
              />
              <div className="pp-turnstile-anchor">
                <Turnstile
                  siteKey="0x4AAAAAADyiilnomapL0Pb7"
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                  options={{ theme: "dark", appearance: "interaction-only" }}
                />
              </div>
            </>
          )}

          {phase === "connecting" && (
            <>
              {isError ? (
                <>
                  <h1 className="pp-headline">Session failed</h1>
                  <div className="pp-mono-lines" style={{ color: "#cc3333" }}>{statusReason}</div>
                  <button className="pp-btn" onClick={handleEndStream}>Try again</button>
                </>
              ) : (
                <>
                  <ControlsDiagram />
                  <div className="pp-connecting">
                    <div className="pp-spinner" />
                    <div className="pp-mono-lines" style={{ color: "#aaa" }}>
                      {status === "ACTIVATING" ? "Loading game..." : "Connecting"}
                    </div>
                    {capacityStatus === "ondemand" && (
                      <div className="pp-mono-lines" style={{ color: "#aaa" }}>
                        Servers are busy — you might need to wait up to 3 minutes to play.
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {!showStreamElements && (
        <footer className="pp-footer">
          <Link to="/privacy" className="pp-read-more">Privacy Notice</Link>
        </footer>
      )}

      {showStreamElements && (
        <div className={`stream-view ${phase === "connecting" ? "stream-view--hidden" : ""}`}>
          <video id="stream-video" autoPlay playsInline muted />
          <audio id="stream-audio" autoPlay />
          {phase === "streaming" && glsInstance && (
            <StreamView glsInstance={glsInstance} onEndStream={handleEndStream} />
          )}
        </div>
      )}
    </div>
  );
}
